// Terminal ao vivo: janela sobre o escritório que espelha a conversa da CLI do agente, no jeito
// que a CLI mostra (› o que você pediu, ● o que o agente respondeu, ⏺ ferramenta com ⎿ a saída),
// com a caixa de mensagem embaixo. Funciona para qualquer agente e qualquer CLI: Claude e Codex
// pelo transcript, OpenCode e MiniMax pelo banco/arquivo deles, e o resto resumido pelas ações
// que o escritório viu (o servidor avisa com source: 'atividades').
//
// Abas: os agentes principais do mesmo projeto. Filtros: tudo, só prompts, sem ferramentas.
// Busca (lupa ou Ctrl/⌘+F) marca o texto e pula entre os achados. Cada item tem copiar e a hora.
// "Assumir daqui" abre a sessão num terminal do escritório (claude --resume); "Encerrar" fecha o
// terminal do escritório dessa sessão. Os dois só para admin (o servidor confere de novo).
//
// Só leitura do lado do servidor (GET /api/agents/:id/transcript a cada LIVE_MS); mandar
// mensagem usa a mesma rota da caixa do painel.
//
// No celular (B29-M4, ehCelular) a janela ocupa a tela inteira, sem arrastar nem redimensionar:
// o cabeçalho enxuto tem ← para voltar e ⋯ com Buscar, Filtros, Assumir e Encerrar, e a caixa de
// mensagem fica sobre o teclado (alturaSobreTeclado, pelo visualViewport). O CSS da versão de
// celular está no fim da @media do modo jogo de style.css.

import type { Agent, TranscriptItem } from '../core/types.js';
import { localeTag, t } from './i18n.js';
import { ehCelular } from './mobile.js';
import { DoisCliques } from './pty-logica.js';
import { acompanhaMensagem, marcaEnvio, pintaSituacao } from './mensagem-situacao.js';

export const LIVE_MS = 2000;

/**
 * Até onde a janela vai, no celular: a altura da tela visível (visualViewport) desconta o teclado.
 * `janela` é innerHeight; `vv` é o visualViewport (height e offsetTop, quando a página rolou com o teclado).
 * Devolve o `bottom` da janela em px (nunca negativo).
 */
export function alturaSobreTeclado(janela: number, vv: { height: number; offsetTop: number }): number {
  return Math.max(0, Math.round(janela - vv.height - vv.offsetTop));
}

/** Sessão encerrada (histórico): lida pela conta e pelo id da sessão, sem agente vivo e sem caixa de mensagem. */
export interface SessaoEncerrada {
  conta: string;
  sessionId: string;
  titulo: string;
  projeto: string;
  /** Último evento (epoch ms): a data no cabeçalho e a hora no rodapé. */
  quando: number;
  /** Selo da conta (sigla e cor). */
  tag: { label: string; color: string };
}

/** Linhas da saída de ferramenta visíveis antes do "… +N linhas". */
export const SAIDA_LINHAS = 6;

export type Filtro = 'tudo' | 'prompts' | 'sem-ferramentas';

export interface LiveDeps {
  /** O agente agora (some quando a sessão sai do escritório). */
  agent: (id: string) => Agent | undefined;
  /** Os outros agentes principais do mesmo projeto (as abas). */
  siblings: (agent: Agent) => Agent[];
  /** Rótulo e cor da CLI (selo no cabeçalho). */
  tag: (agent: Agent) => { label: string; color: string };
  /** Quem entrou é admin (mandar mensagem, assumir, encerrar). */
  isAdmin: () => boolean;
  /** "Assumir daqui": a sessão num terminal do escritório. */
  takeover: (agent: Agent) => void;
  /** Id do terminal do escritório que roda esta sessão, se houver (para "Encerrar"). */
  officeTerminal: (agent: Agent) => string | undefined;
  /** Fecha o terminal do escritório. */
  stopTerminal: (id: string) => Promise<void>;
  /** Abriu/fechou (o painel do agente redesenha o botão). */
  onChange?: () => void;
  /** Intervalo de releitura (ms), do CMS; sem ele, LIVE_MS. */
  refreshMs?: () => number;
  /** Terminal interativo (6.1): quando o agente tem um pty, a janela mostra o xterm no lugar da conversa. */
  pty?: LivePty;
}

/** O que a janela precisa do terminal interativo (montado em main.ts com src/web/pty.ts). */
export interface LivePty {
  /** Id do pty que roda este agente, se houver. */
  sessao: (agent: Agent) => string | undefined;
  /** O servidor liberou o terminal interativo para quem entrou (admin, TOTP, interruptor, node-pty)? */
  disponivel: () => boolean;
  /** Monta o xterm da sessão dentro de `host` (ele chama destroy ao trocar de agente ou fechar). */
  monta: (host: HTMLElement, id: string) => { destroy: () => void };
  /** Encerra: o pty (id) ou, sem ele, a sessão do Claude aberta noutro terminal. Devolve o erro, se houver. */
  encerra: (agent: Agent, id: string | undefined) => Promise<string | undefined>;
}

interface Resposta {
  ok?: boolean;
  items?: TranscriptItem[];
  source?: 'conversa' | 'atividades';
  error?: string;
}

const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

/** Markdown de bolso das respostas: blocos ```, `código`, **negrito**. Escapa antes de tudo. */
export function markdown(text: string): string {
  const partes = text.split(/```[^\n]*\n?/);
  return partes
    .map((parte, i) => {
      if (i % 2 === 1) return `<pre class="vivo-bloco">${esc(parte.replace(/\n$/, ''))}</pre>`;
      return esc(parte)
        .replace(/`([^`\n]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>')
        .split(/\n{2,}/)
        .map((p) => (p.trim() ? `<p>${p.replace(/\n/g, '<br>')}</p>` : ''))
        .join('');
    })
    .join('');
}

/** O item entra no filtro? */
export function passa(item: TranscriptItem, filtro: Filtro): boolean {
  if (filtro === 'prompts') return item.role === 'voce';
  if (filtro === 'sem-ferramentas') return item.role !== 'ferramenta';
  return true;
}

/** Um item no formato do terminal da CLI (puro: testado em tests/live.test.ts). */
export function linhaHtml(item: TranscriptItem, quando = ''): string {
  const extra = `<button class="vivo-copiar" type="button" data-copiar aria-label="${esc(t('vivo.copiar'))}" title="${esc(t('vivo.copiar'))}">⧉</button>${quando ? `<time>${esc(quando)}</time>` : ''}`;
  if (item.role === 'voce') return `<li class="vivo-voce"><span class="vivo-marca">›</span><div class="vivo-corpo"><pre>${esc(item.text)}</pre></div>${extra}</li>`;
  if (item.role === 'agente') return `<li class="vivo-agente"><span class="vivo-marca">●</span><div class="vivo-corpo">${markdown(item.text)}</div>${extra}</li>`;
  const [cabeca = '', ...resto] = item.text.split('\n');
  const saida = resto.join('\n').trim();
  let corpo = '';
  if (saida) {
    const linhas = saida.split('\n');
    const curto = linhas.slice(0, SAIDA_LINHAS).join('\n');
    const mais = linhas.length - SAIDA_LINHAS;
    corpo =
      mais > 0
        ? `<details class="vivo-saida"><summary><span class="vivo-l">⎿</span><pre>${esc(curto)}</pre><span class="vivo-mais">… +${mais}</span></summary><pre>${esc(linhas.slice(SAIDA_LINHAS).join('\n'))}</pre></details>`
        : `<div class="vivo-saida"><span class="vivo-l">⎿</span><pre>${esc(curto)}</pre></div>`;
  }
  return `<li class="vivo-ferramenta"><span class="vivo-marca">⏺</span><div class="vivo-corpo"><pre class="vivo-cabeca">${esc(cabeca)}</pre>${corpo}</div>${extra}</li>`;
}

export class LiveTerminal {
  readonly el: HTMLElement;
  private agentId: string | null = null;
  /** Sessão encerrada aberta (sem agente vivo); null quando a janela mostra um agente. */
  private encerrada: SessaoEncerrada | null = null;
  private timer: number | undefined;
  private assinatura = '';
  private itens: TranscriptItem[] = [];
  private filtro: Filtro = 'tudo';
  private busca = '';
  private achado = 0;
  /** O celular pôs top e bottom no estilo da janela (a volta ao desktop tira). */
  private ajustado = false;
  /** O xterm do pty do agente aberto (6.1), se a janela está mostrando um. */
  private ptyTela: { id: string; destroy: () => void } | null = null;
  private readonly ptyConfirma = new DoisCliques();
  /** Para o acompanhamento da última mensagem enviada (uma por vez). */
  private pararSituacao?: () => void;

  constructor(
    host: HTMLElement,
    private readonly deps: LiveDeps,
  ) {
    this.el = document.createElement('section');
    this.el.className = 'vivo';
    this.el.hidden = true;
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-label', t('vivo.titulo'));
    // o ← e o ⋯ só aparecem no celular (hidden aqui; style.css os mostra na versão de celular)
    this.el.innerHTML = `<header class="vivo-cab">
        <button class="vivo-voltar" type="button" hidden aria-label="${esc(t('vivo.voltar'))}" title="${esc(t('vivo.voltar'))}">←</button>
        <span class="vivo-logo" aria-hidden="true">&gt;_</span><span class="vivo-marca-txt">${esc(t('vivo.titulo'))}</span>
        <b class="vivo-nome"></b><span class="vivo-papel"></span><span class="vivo-tag"></span><span class="vivo-onde"></span>
        <span class="vivo-acoes">
          <button class="vivo-lupa" type="button" aria-label="${esc(t('vivo.buscar'))}" title="${esc(t('vivo.buscar'))} (Ctrl+F)">⌕</button>
          <button class="vivo-assumir" type="button">${esc(t('vivo.assumir'))}</button>
          <button class="vivo-encerrar" type="button">${esc(t('vivo.encerrar'))}</button>
          <button class="vivo-min" type="button" aria-label="${esc(t('vivo.minimizar'))}" title="${esc(t('vivo.minimizar'))}">▁</button>
          <button class="vivo-max" type="button" aria-label="${esc(t('vivo.maximizar'))}" title="${esc(t('vivo.maximizar'))}">⤢</button>
          <button class="vivo-fechar" type="button" aria-label="${esc(t('acao.fechar'))}">✕</button>
          <span class="vivo-menu-wrap" hidden><button class="vivo-menu-botao" type="button" aria-haspopup="menu" aria-expanded="false" aria-label="${esc(t('vivo.menu'))}" title="${esc(t('vivo.menu'))}">⋯</button><span class="vivo-menu" role="menu" hidden>
            <button type="button" role="menuitem" data-menu="lupa">${esc(t('vivo.buscar'))}</button>
            <button type="button" role="menuitem" data-menu="filtros">${esc(t('vivo.filtros'))}</button>
            <button type="button" role="menuitem" data-menu="assumir" class="vivo-m-assumir">${esc(t('vivo.assumir'))}</button>
            <button type="button" role="menuitem" data-menu="encerrar" class="vivo-m-encerrar">${esc(t('vivo.encerrar'))}</button>
          </span></span>
        </span></header>
      <nav class="vivo-abas" role="tablist"></nav>
      <div class="vivo-pty" hidden></div>
      <div class="vivo-barra">
        <div class="vivo-filtros" role="group">
          <button type="button" data-filtro="tudo">${esc(t('vivo.tudo'))}</button>
          <button type="button" data-filtro="prompts">${esc(t('vivo.so_prompts'))}</button>
          <button type="button" data-filtro="sem-ferramentas">${esc(t('vivo.sem_ferramentas'))}</button>
        </div>
        <form class="vivo-busca" hidden><input type="search" placeholder="${esc(t('vivo.buscar'))}" aria-label="${esc(t('vivo.buscar'))}" /><span class="vivo-contagem"></span><button type="button" data-busca="-1" aria-label="↑">↑</button><button type="button" data-busca="1" aria-label="↓">↓</button></form>
      </div>
      <p class="vivo-aviso" hidden></p>
      <ol class="vivo-lista" tabindex="0"></ol>
      <p class="vivo-status"></p>
      <form class="vivo-envio"><span class="vivo-prompt" aria-hidden="true">&gt;</span><textarea rows="1"></textarea><button type="submit" aria-label="${esc(t('det.enviar'))}">↵</button></form>
      <p class="vivo-situacao situacao-mensagem" aria-live="polite" hidden></p>
      <p class="vivo-erro" role="alert"></p>`;
    host.append(this.el);
    this.bind();
    // o teclado do celular encolhe a tela visível: a caixa de mensagem acompanha (só mexe no celular)
    const vv = window.visualViewport;
    vv?.addEventListener('resize', () => this.ajustaTeclado());
    vv?.addEventListener('scroll', () => this.ajustaTeclado());
    window.addEventListener('resize', () => this.ajustaTeclado());
  }

  get openAgent(): string | null {
    return this.agentId;
  }

  get isOpen(): boolean {
    return this.chave() !== null;
  }

  open(agentId: string): void {
    this.mostra(agentId, null);
  }

  /** Abre uma sessão encerrada (histórico): a conversa vem de /api/sessions/:conta/:sessionId/transcript. */
  openEncerrada(sessao: SessaoEncerrada): void {
    this.mostra(null, sessao);
  }

  close(): void {
    window.clearInterval(this.timer);
    this.timer = undefined;
    this.agentId = null;
    this.encerrada = null;
    this.sincronizaPty(undefined);
    this.menu(false);
    this.el.hidden = true;
    this.deps.onChange?.();
  }

  /** O escritório mudou (status, atividade, agentes): cabeçalho, abas e rodapé sem buscar de novo. */
  update(): void {
    if (this.chave()) this.cabecalho();
  }

  // ------------------------------------------------------------------ interno

  /** O que a janela mostra agora: o id do agente, ou a sessão encerrada; null se fechada. */
  private chave(): string | null {
    if (this.encerrada) return `encerrada:${this.encerrada.conta}:${this.encerrada.sessionId}`;
    return this.agentId;
  }

  private mostra(agentId: string | null, encerrada: SessaoEncerrada | null): void {
    const antes = this.chave();
    this.agentId = agentId;
    this.encerrada = encerrada;
    if (antes !== this.chave()) {
      this.assinatura = '';
      this.itens = [];
      this.q('.vivo-lista').innerHTML = `<li class="vivo-vazio">${esc(t('conversa.carregando'))}</li>`;
      this.q('.vivo-erro').textContent = '';
    }
    this.el.hidden = false;
    this.ajustaTeclado();
    this.cabecalho();
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => void this.refresh(), this.deps.refreshMs?.() ?? LIVE_MS);
    void this.refresh();
    // no celular não foca a caixa: o teclado não sobe sozinho ao abrir a conversa
    if (this.deps.isAdmin() && !encerrada && !ehCelular()) this.q<HTMLTextAreaElement>('.vivo-envio textarea').focus();
    this.deps.onChange?.();
  }

  /**
   * Celular: a janela vai do topo da tela visível até o teclado (alturaSobreTeclado). `important` vence
   * o inset do CSS da versão de celular. No desktop não mexe em nada, salvo para tirar o que o celular pôs.
   */
  private ajustaTeclado(): void {
    if (!ehCelular()) {
      if (this.ajustado) {
        this.el.style.removeProperty('top');
        this.el.style.removeProperty('bottom');
        this.ajustado = false;
      }
      return;
    }
    const vv = window.visualViewport;
    const alvo = vv ?? { height: window.innerHeight, offsetTop: 0 };
    this.el.style.setProperty('top', `${alvo.offsetTop}px`, 'important');
    this.el.style.setProperty('bottom', `${alturaSobreTeclado(window.innerHeight, alvo)}px`, 'important');
    this.ajustado = true;
  }

  /** O menu ⋯ do celular, aberto ou fechado. */
  private menu(aberto: boolean): void {
    this.q('.vivo-menu').hidden = !aberto;
    this.q('.vivo-menu-botao').setAttribute('aria-expanded', String(aberto));
  }

  private q<T extends HTMLElement = HTMLElement>(sel: string): T {
    return this.el.querySelector(sel) as T;
  }

  private bind(): void {
    this.q('.vivo-fechar').addEventListener('click', () => this.close());
    this.q('.vivo-voltar').addEventListener('click', () => this.close());
    // (hidden pode ser 'until-found' no TypeScript novo: por isso o !!)
    this.q('.vivo-menu-botao').addEventListener('click', () => this.menu(!!this.q('.vivo-menu').hidden));
    // o menu ⋯ repete as ações do cabeçalho (clica nos botões dele); fechar tocando fora
    this.q('.vivo-menu').addEventListener('click', (e) => {
      const item = (e.target as HTMLElement).closest<HTMLElement>('[data-menu]')?.dataset.menu;
      if (!item) return;
      this.menu(false);
      if (item === 'filtros') this.el.classList.toggle('com-barra');
      else if (item === 'lupa') this.abreBusca();
      else this.q(item === 'assumir' ? '.vivo-assumir' : '.vivo-encerrar').click();
    });
    this.el.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.vivo-menu-wrap')) this.menu(false);
    });
    this.q('.vivo-max').addEventListener('click', () => {
      this.el.classList.remove('min');
      this.el.classList.toggle('max');
    });
    this.q('.vivo-min').addEventListener('click', () => {
      this.el.classList.remove('max');
      this.el.classList.toggle('min');
    });
    this.arrasta();
    this.q('.vivo-lupa').addEventListener('click', () => this.abreBusca());
    this.q('.vivo-assumir').addEventListener('click', () => {
      const agent = this.agentId ? this.deps.agent(this.agentId) : undefined;
      if (agent) this.deps.takeover(agent);
    });
    this.q('.vivo-encerrar').addEventListener('click', () => {
      const agent = this.agentId ? this.deps.agent(this.agentId) : undefined;
      if (agent && this.encerraPelaPty(agent)) return void this.encerraPty(agent);
      const id = agent ? this.deps.officeTerminal(agent) : undefined;
      if (agent && id && confirm(t('vivo.encerrar_confirma', { nome: agent.name }))) void this.deps.stopTerminal(id).then(() => this.cabecalho());
    });
    this.q('.vivo-abas').addEventListener('click', (e) => {
      const aba = (e.target as HTMLElement).closest<HTMLElement>('[data-aba]');
      if (aba?.dataset.aba) this.open(aba.dataset.aba);
    });
    this.q('.vivo-filtros').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-filtro]');
      if (!b) return;
      this.filtro = b.dataset.filtro as Filtro;
      this.pinta(true);
    });
    const busca = this.q<HTMLInputElement>('.vivo-busca input');
    busca.addEventListener('input', () => {
      this.busca = busca.value.trim().toLowerCase();
      this.achado = 0;
      this.pinta(false);
    });
    busca.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        this.pula(e.shiftKey ? -1 : 1);
      } else if (e.key === 'Escape') this.fechaBusca();
    });
    this.q('.vivo-busca').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-busca]');
      if (b) this.pula(Number(b.dataset.busca));
    });
    this.q('.vivo-lista').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-copiar]');
      if (!b) return;
      const li = b.closest('li');
      const texto = li?.querySelector('.vivo-corpo')?.textContent ?? '';
      void navigator.clipboard?.writeText(texto.trim()).then(() => {
        b.textContent = '✓';
        setTimeout(() => (b.textContent = '⧉'), 1500);
      });
    });
    const form = this.q<HTMLFormElement>('.vivo-envio');
    const area = this.q<HTMLTextAreaElement>('.vivo-envio textarea');
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.send(area);
    });
    area.addEventListener('input', () => {
      area.style.height = 'auto';
      area.style.height = `${Math.min(area.scrollHeight, 160)}px`;
    });
    area.addEventListener('keydown', (e) => {
      // as teclas daqui não viram atalho do escritório (T, J, K…)
      e.stopPropagation();
      if (e.key === 'Escape') return this.close();
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        return this.abreBusca();
      }
      if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return;
      e.preventDefault();
      void this.send(area);
    });
    this.el.addEventListener('keydown', (e) => {
      // no xterm as teclas são do programa que roda lá (Esc e Ctrl+F inclusive)
      if ((e.target as HTMLElement).closest('.vivo-pty')) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        this.abreBusca();
      } else if (e.key === 'Escape' && !(e.target as HTMLElement).closest('.vivo-busca')) this.close();
    });
  }

  /** Arrastar pelo cabeçalho reposiciona a janela (fica dentro da tela). Duplo clique maximiza. */
  private arrasta(): void {
    const cab = this.q('.vivo-cab');
    cab.addEventListener('dblclick', (e) => {
      if ((e.target as HTMLElement).closest('button') || ehCelular()) return;
      this.el.classList.remove('min');
      this.el.classList.toggle('max');
    });
    cab.addEventListener('pointerdown', (e) => {
      // no celular a janela é a tela inteira: não se arrasta
      if ((e.target as HTMLElement).closest('button') || this.el.classList.contains('max') || e.button !== 0 || ehCelular()) return;
      const caixa = this.el.getBoundingClientRect();
      const dx = e.clientX - caixa.left;
      const dy = e.clientY - caixa.top;
      cab.setPointerCapture(e.pointerId);
      this.el.classList.add('movendo');
      const mover = (ev: PointerEvent) => {
        const x = Math.max(0, Math.min(window.innerWidth - 120, ev.clientX - dx));
        const y = Math.max(0, Math.min(window.innerHeight - 40, ev.clientY - dy));
        Object.assign(this.el.style, { left: `${x}px`, top: `${y}px`, right: 'auto', bottom: 'auto' });
      };
      const soltar = () => {
        this.el.classList.remove('movendo');
        cab.removeEventListener('pointermove', mover);
        cab.removeEventListener('pointerup', soltar);
        try {
          localStorage.setItem('agzos.vivo.pos', JSON.stringify({ left: this.el.style.left, top: this.el.style.top, width: this.el.style.width, height: this.el.style.height }));
        } catch {
          // sem armazenamento: a posição vale só nesta página
        }
      };
      cab.addEventListener('pointermove', mover);
      cab.addEventListener('pointerup', soltar);
    });
    try {
      const pos = JSON.parse(localStorage.getItem('agzos.vivo.pos') ?? 'null') as Record<string, string> | null;
      if (pos?.left && pos.top && !ehCelular()) Object.assign(this.el.style, { left: pos.left, top: pos.top, right: 'auto', bottom: 'auto' });
    } catch {
      // posição padrão
    }
  }

  private abreBusca(): void {
    const form = this.q('.vivo-busca');
    form.hidden = false;
    // no celular a busca fica na barra de filtros, que está recolhida até Buscar ou Filtros
    this.el.classList.add('com-barra');
    this.q<HTMLInputElement>('.vivo-busca input').focus();
  }

  private fechaBusca(): void {
    this.q('.vivo-busca').hidden = true;
    this.q<HTMLInputElement>('.vivo-busca input').value = '';
    this.busca = '';
    this.pinta(false);
  }

  private cabecalho(): void {
    if (this.encerrada) return this.cabecalhoEncerrada(this.encerrada);
    const agent = this.agentId ? this.deps.agent(this.agentId) : undefined;
    if (!agent) {
      this.q('.vivo-status').textContent = t('vivo.saiu');
      return;
    }
    const tag = this.deps.tag(agent);
    this.q('.vivo-nome').textContent = agent.name;
    this.q('.vivo-papel').textContent = agent.parentId ? t('vivo.subagente') : t('vivo.principal');
    const el = this.q('.vivo-tag');
    el.textContent = tag.label;
    el.style.background = tag.color;
    this.q('.vivo-onde').textContent = t('vivo.sala', { nome: agent.project });
    this.el.dataset.status = agent.status;
    const ocupado = agent.status === 'working' || agent.status === 'thinking';
    const status = this.q('.vivo-status');
    status.className = `vivo-status ${ocupado ? 'ocupado' : ''}`;
    status.textContent = ocupado ? `✻ ${agent.activity || t('vivo.trabalhando')}…` : agent.status === 'waiting' ? `⏸ ${t('vivo.esperando')}` : `○ ${t('vivo.aguardando')}`;
    const admin = this.deps.isAdmin();
    this.q('.vivo-envio').hidden = !admin;
    const area = this.q<HTMLTextAreaElement>('.vivo-envio textarea');
    // no celular a dica longa (Enter manda, Shift+Enter…) quebra a linha: lá vai só o nome
    area.placeholder = window.matchMedia('(max-width: 760px)').matches ? t('det.placeholder', { nome: agent.name }) : t('vivo.placeholder', { nome: agent.name });
    // agente fictício (demonstração) não tem sessão de verdade para assumir
    this.q('.vivo-assumir').hidden = !admin || agent.provider !== 'claude' || agent.id.startsWith('demo:');
    const doPty = admin ? this.deps.pty?.sessao(agent) : undefined;
    this.q('.vivo-encerrar').hidden = !admin || !(this.deps.officeTerminal(agent) || doPty || this.encerraPelaPty(agent));
    this.sincronizaPty(doPty);
    this.espelhaMenu();
    // abas: este agente e os outros principais do projeto
    const abas = [agent, ...this.deps.siblings(agent).filter((a) => a.id !== agent.id)];
    this.q('.vivo-abas').innerHTML = abas
      .map((a) => `<button type="button" role="tab" data-aba="${esc(a.id)}" aria-selected="${a.id === agent.id}" class="vivo-aba st-${esc(a.status)}"><span class="vivo-aba-ponto"></span><b>${esc(a.name)}</b>${a.title ? `<span>${esc(a.title)}</span>` : ''}</button>`)
      .join('');
    this.q('.vivo-abas').hidden = abas.length < 2;
    for (const b of this.el.querySelectorAll<HTMLElement>('[data-filtro]')) b.setAttribute('aria-pressed', String(b.dataset.filtro === this.filtro));
  }

  /** Sessão encerrada: cabeçalho com título, data e projeto; rodapé com a hora; sem caixa de mensagem. */
  private cabecalhoEncerrada(sessao: SessaoEncerrada): void {
    const hora = new Date(sessao.quando).toLocaleTimeString(localeTag(), { hour: '2-digit', minute: '2-digit' });
    this.el.dataset.status = 'encerrada';
    this.q('.vivo-nome').textContent = sessao.titulo || t('historico.sem_titulo');
    this.q('.vivo-papel').textContent = new Date(sessao.quando).toLocaleDateString(localeTag(), { day: 'numeric', month: 'long', year: 'numeric' });
    const el = this.q('.vivo-tag');
    el.textContent = sessao.tag.label;
    el.style.background = sessao.tag.color;
    this.q('.vivo-onde').textContent = t('vivo.sala', { nome: sessao.projeto });
    const status = this.q('.vivo-status');
    status.className = 'vivo-status';
    status.textContent = t('vivo.encerrada', { hora });
    this.sincronizaPty(undefined);
    this.q('.vivo-envio').hidden = true;
    this.q('.vivo-assumir').hidden = true;
    this.q('.vivo-encerrar').hidden = true;
    this.espelhaMenu();
    this.q('.vivo-abas').hidden = true;
    this.q('.vivo-aviso').hidden = true;
  }

  /** O Encerrar vale pelo terminal interativo (pty, ou o Claude aberto noutro terminal) e não pelo tmux? */
  private encerraPelaPty(agent: Agent): boolean {
    const pty = this.deps.pty;
    if (!pty) return false;
    return Boolean(pty.sessao(agent)) || (pty.disponivel() && agent.provider === 'claude' && !this.deps.officeTerminal(agent));
  }

  /** Mostra o xterm do pty (sessao) no lugar da conversa, ou volta à conversa (undefined). */
  private sincronizaPty(sessao: string | undefined): void {
    if (this.ptyTela?.id !== sessao) {
      this.ptyTela?.destroy();
      this.ptyTela = null;
      const host = this.q('.vivo-pty');
      host.replaceChildren();
      if (sessao && this.deps.pty) this.ptyTela = { id: sessao, destroy: this.deps.pty.monta(host, sessao).destroy };
    }
    this.q('.vivo-pty').hidden = !this.ptyTela;
    this.el.classList.toggle('com-pty', Boolean(this.ptyTela));
  }

  /** Encerrar pelo terminal interativo: confirmação em dois cliques no próprio botão. */
  private encerraPty(agent: Agent): void {
    const botao = this.q('.vivo-encerrar');
    const volta = () => {
      this.ptyConfirma.desarma();
      botao.textContent = t('vivo.encerrar');
      botao.classList.remove('armado');
    };
    if (!this.ptyConfirma.clica()) {
      botao.textContent = t('pty.confirmar');
      botao.classList.add('armado');
      window.setTimeout(() => this.ptyConfirma.armado && volta(), 4000);
      return;
    }
    volta();
    void this.deps.pty?.encerra(agent, this.ptyTela?.id).then((erro) => {
      this.q('.vivo-erro').textContent = erro ?? '';
      this.cabecalho();
    });
  }

  /** O menu ⋯ mostra Assumir e Encerrar só quando o cabeçalho mostra (mesmo critério). */
  private espelhaMenu(): void {
    this.q('[data-menu="assumir"]').hidden = this.q('.vivo-assumir').hidden;
    this.q('[data-menu="encerrar"]').hidden = this.q('.vivo-encerrar').hidden;
  }

  private async refresh(): Promise<void> {
    const id = this.chave();
    // a situação da última mensagem segue o agente aberto (outro agente = linha escondida)
    if (this.agentId) pintaSituacao(this.el.querySelector<HTMLElement>('.vivo-situacao'), this.agentId, t);
    if (!id) return;
    const url = this.encerrada
      ? `/api/sessions/${encodeURIComponent(this.encerrada.conta)}/${encodeURIComponent(this.encerrada.sessionId)}/transcript?limit=150`
      : `/api/agents/${encodeURIComponent(id)}/transcript?limit=150`;
    const res = await fetch(url).catch(() => null);
    const json = (await res?.json().catch(() => undefined)) as Resposta | undefined;
    if (this.chave() !== id) return; // fechou ou trocou enquanto a resposta vinha
    this.cabecalho();
    const aviso = this.q('.vivo-aviso');
    aviso.hidden = json?.source !== 'atividades';
    aviso.textContent = t('vivo.resumo');
    if (!json?.ok || !json.items) {
      this.q('.vivo-erro').textContent = json?.error ?? t('erro.nao_deu');
      return;
    }
    this.q('.vivo-erro').textContent = '';
    const assinatura = `${json.items.length}:${json.items.at(-1)?.at ?? 0}:${json.items.at(-1)?.text.length ?? 0}`;
    if (assinatura === this.assinatura) return;
    this.assinatura = assinatura;
    this.itens = json.items;
    this.pinta(false);
  }

  /** Redesenha a lista (filtro e busca). `topo`: depois de trocar o filtro, vai para o fim. */
  private pinta(fim: boolean): void {
    const lista = this.q('.vivo-lista');
    // quem estava no fim segue as mensagens novas; quem subiu para ler fica onde está
    const noFim = fim || lista.scrollHeight - lista.scrollTop - lista.clientHeight < 32;
    const topo = lista.scrollTop;
    const visiveis = this.itens.filter((i) => passa(i, this.filtro) && (!this.busca || i.text.toLowerCase().includes(this.busca)));
    const hora = (at: number) => new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    lista.innerHTML = visiveis.length ? visiveis.map((i) => linhaHtml(i, hora(i.at))).join('') : `<li class="vivo-vazio">${esc(this.busca ? t('vivo.nada_achado') : t('conversa.vazia'))}</li>`;
    if (this.busca) this.marca(lista);
    this.q('.vivo-contagem').textContent = this.busca ? `${visiveis.length}` : '';
    for (const b of this.el.querySelectorAll<HTMLElement>('[data-filtro]')) b.setAttribute('aria-pressed', String(b.dataset.filtro === this.filtro));
    if (this.busca) this.pula(0);
    else lista.scrollTop = noFim ? lista.scrollHeight : topo;
  }

  /** Marca o termo buscado nos textos (só nós de texto: o HTML continua escapado). */
  private marca(lista: HTMLElement): void {
    const walker = document.createTreeWalker(lista, NodeFilter.SHOW_TEXT);
    const nos: Text[] = [];
    while (walker.nextNode()) nos.push(walker.currentNode as Text);
    for (const no of nos) {
      if (no.parentElement?.closest('button, time')) continue;
      const texto = no.data;
      const i = texto.toLowerCase().indexOf(this.busca);
      if (i < 0) continue;
      const mark = document.createElement('mark');
      const depois = no.splitText(i);
      depois.splitText(this.busca.length);
      mark.textContent = depois.data;
      depois.replaceWith(mark);
    }
  }

  private pula(delta: number): void {
    const marcas = [...this.el.querySelectorAll<HTMLElement>('.vivo-lista mark')];
    if (!marcas.length) return;
    this.achado = (this.achado + delta + marcas.length) % marcas.length;
    marcas.forEach((m, i) => m.classList.toggle('atual', i === this.achado));
    marcas[this.achado]?.scrollIntoView({ block: 'center' });
    this.q('.vivo-contagem').textContent = `${this.achado + 1}/${marcas.length}`;
  }

  private async send(area: HTMLTextAreaElement): Promise<void> {
    const text = area.value.trim();
    if (!text || !this.agentId) return;
    const res = await fetch(`/api/agents/${encodeURIComponent(this.agentId)}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    }).catch(() => null);
    const json = (await res?.json().catch(() => ({}))) as { ok?: boolean; error?: string; id?: string } | undefined;
    if (json?.ok) {
      area.value = '';
      area.style.height = 'auto';
      this.q('.vivo-erro').textContent = '';
      // situação ao lado da caixa: na fila, entregue ou não entregue (acompanha o id da mensagem)
      if (json.id) {
        const agentId = this.agentId;
        this.pararSituacao?.();
        marcaEnvio(agentId, json.id);
        this.pararSituacao = acompanhaMensagem(agentId, json.id, () => pintaSituacao(this.el.querySelector<HTMLElement>('.vivo-situacao'), agentId, t));
      }
      void this.refresh();
    } else this.q('.vivo-erro').textContent = json?.error ?? t('erro.nao_deu');
  }
}
